import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  dolapGetir,
  dolapGuncelle,
  dolapOlustur,
  dolapSil,
  dolapTopluGuncelle,
  dolapTopluUret,
  dolapZorlaSifirla,
  dolaplariListele,
  type DolapGuncelleIstegi,
  type DolapOlusturIstegi,
  type DolapSorgusu,
  type DolapTopluGuncelleIstegi,
  type DolapTopluUretIstegi,
  type SayfalanmisDolaplar,
} from '@emanetx/sdk';
import { istemci } from '../istemci.js';

const ANAHTARLAR = {
  liste: (sorgu: Partial<DolapSorgusu>) => ['dolaplar', 'liste', sorgu] as const,
  detay: (id: string) => ['dolaplar', 'detay', id] as const,
};

export function useDolapListesi(sorgu: Partial<DolapSorgusu>) {
  return useQuery<SayfalanmisDolaplar>({
    queryKey: ANAHTARLAR.liste(sorgu),
    queryFn: () => dolaplariListele(istemci, sorgu),
    placeholderData: (onceki) => onceki,
  });
}

export function useDolap(id: string | undefined) {
  return useQuery({
    queryKey: ANAHTARLAR.detay(id ?? ''),
    queryFn: () => dolapGetir(istemci, id as string),
    enabled: !!id,
  });
}

export function useDolapOlustur() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (giris: DolapOlusturIstegi) => dolapOlustur(istemci, giris),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dolaplar'] }),
  });
}

export function useDolapGuncelle(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (giris: DolapGuncelleIstegi) => dolapGuncelle(istemci, id, giris),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dolaplar'] }),
  });
}

export function useDolapSil() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => dolapSil(istemci, id),
    onSuccess: (_, id) => {
      // Silinen kaydı detay cache'inden cikar (yoksa modal kapanırken refetch 404 atar).
      qc.removeQueries({ queryKey: ANAHTARLAR.detay(id) });
      qc.invalidateQueries({ queryKey: ['dolaplar', 'liste'] });
      qc.invalidateQueries({ queryKey: ['noktalar'] });
    },
  });
}

export function useDolapZorlaSifirla() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      sebep,
      yeniDurum,
    }: {
      id: string;
      sebep: string;
      yeniDurum?: 'MUSAIT' | 'MESGUL' | 'REZERVE' | 'BAKIM' | 'CEVRIMDISI';
    }) => dolapZorlaSifirla(istemci, id, sebep, yeniDurum ?? 'MUSAIT'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dolaplar'] });
      qc.invalidateQueries({ queryKey: ['noktalar'] });
    },
  });
}

export function useDolapTopluUret() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (giris: DolapTopluUretIstegi) => dolapTopluUret(istemci, giris),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dolaplar'] });
      qc.invalidateQueries({ queryKey: ['noktalar'] });
    },
  });
}

export function useDolapTopluGuncelle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (giris: DolapTopluGuncelleIstegi) => dolapTopluGuncelle(istemci, giris),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dolaplar'] });
    },
  });
}
